import { useState } from 'react';
import { Link, useForm } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import PageHeader from '@/Components/PageHeader';
import FormInput from '@/Components/Form/FormInput';
import FormSection from '@/Components/Form/FormSection';

export default function UsersCreate({ roles }) {
    const [showPassword, setShowPassword] = useState(false);

    const { data, setData, post, processing, errors } = useForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
        roles: [],
        is_active: true,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        post('/users');
    };

    const handleRoleChange = (roleName) => {
        const newRoles = data.roles.includes(roleName)
            ? data.roles.filter(r => r !== roleName)
            : [...data.roles, roleName];
        setData('roles', newRoles);
    };

    return (
        <AppLayout title="Create User">
            <PageHeader
                title="Create User"
                subtitle="Add a new user to the system"
                breadcrumbs={[
                    { label: 'Settings' },
                    { label: 'Users', href: '/users' },
                    { label: 'Create' },
                ]}
            />

            <div className="row">
                <div className="col-lg-8">
                    <form onSubmit={handleSubmit}>
                        <FormSection title="User Information">
                            <FormInput
                                className="col-md-6"
                                label="Full Name"
                                name="name"
                                value={data.name}
                                onChange={(v) => setData('name', v)}
                                error={errors.name}
                                required
                                placeholder="Enter full name"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Email Address"
                                name="email"
                                type="email"
                                value={data.email}
                                onChange={(v) => setData('email', v)}
                                error={errors.email}
                                required
                                placeholder="Enter email address"
                            />
                            <FormInput
                                className="col-md-6"
                                label="Phone Number"
                                name="phone"
                                value={data.phone}
                                onChange={(v) => setData('phone', v)}
                                error={errors.phone}
                                placeholder="Enter phone number"
                            />
                            <div className="col-md-6">
                                <label className="form-label">Status</label>
                                <div className="form-check form-switch mt-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="is_active"
                                        checked={data.is_active}
                                        onChange={(e) => setData('is_active', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="is_active">
                                        Active
                                    </label>
                                </div>
                            </div>
                        </FormSection>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Password</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Password <span className="text-danger">*</span></label>
                                        <div className="input-group">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                                value={data.password}
                                                onChange={(e) => setData('password', e.target.value)}
                                                placeholder="Enter password"
                                            />
                                            <button
                                                type="button"
                                                className="input-group-text"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                            </button>
                                        </div>
                                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            className="form-control"
                                            value={data.password_confirmation}
                                            onChange={(e) => setData('password_confirmation', e.target.value)}
                                            placeholder="Confirm password"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Roles <span className="text-danger">*</span></h5>
                            </div>
                            <div className="card-body">
                                {errors.roles && (
                                    <div className="alert alert-danger py-2 mb-3">{errors.roles}</div>
                                )}
                                <div className="row g-3">
                                    {roles.map((role) => (
                                        <div key={role.id} className="col-md-4">
                                            <div className={`card h-100 cursor-pointer ${data.roles.includes(role.name) ? 'border-primary bg-primary-subtle' : ''}`}
                                                onClick={() => handleRoleChange(role.name)}
                                            >
                                                <div className="card-body py-3">
                                                    <div className="form-check mb-0">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={data.roles.includes(role.name)}
                                                            onChange={() => handleRoleChange(role.name)}
                                                        />
                                                        <label className="form-check-label fw-semibold">
                                                            {role.name}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={processing}>
                                {processing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-lg me-1"></i>
                                        Create User
                                    </>
                                )}
                            </button>
                            <Link href="/users" className="btn btn-outline-secondary">
                                Cancel
                            </Link>
                        </div>
                    </form>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Role Descriptions</h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <h6 className="mb-1"><span className="badge bg-danger me-2">Super Admin</span></h6>
                                <small className="text-muted">Full system access including role management</small>
                            </div>
                            <div className="mb-3">
                                <h6 className="mb-1"><span className="badge bg-warning text-dark me-2">Admin</span></h6>
                                <small className="text-muted">Full access except role management</small>
                            </div>
                            <div className="mb-3">
                                <h6 className="mb-1"><span className="badge badge-primary me-2">Manager</span></h6>
                                <small className="text-muted">Manage CRM & Travel modules, view reports</small>
                            </div>
                            <div className="mb-3">
                                <h6 className="mb-1"><span className="badge badge-info me-2">Agent</span></h6>
                                <small className="text-muted">Create and manage bookings & contacts</small>
                            </div>
                            <div>
                                <h6 className="mb-1"><span className="badge badge-secondary me-2">Viewer</span></h6>
                                <small className="text-muted">Read-only access to all modules</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}
